// pages/Newsletters/NewsletterDetail.jsx
import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DOMPurify from 'dompurify';

import { newslettersApi, normalizeApiError } from '../../services/api';
import {
  Button,
  Badge,
  NoticeModal,
  ConfirmModal,
  Spinner,
  EmptyStateCard,
  PageHeader,
} from '../../components/common';
import { useIsMobile } from '../../hooks';
import { colors, spacing, borderRadius, shadows } from '../../styles/theme';
import { pageStyles } from "../../styles/pageStyles";
import { Send, Mail, RefreshCw } from 'lucide-react';

const STATUS_MAP = {
  scheduled: { label: 'Запланирована', variant: 'primary' },
  partial: { label: 'Частично отправлена', variant: 'primary' },
  sending: { label: 'Отправка', variant: 'warning' },
  sent: { label: 'Завершена', variant: 'success' },
  failed: { label: 'Ошибка', variant: 'danger' },
};

const CHANNEL_LABELS = {
  'email': 'Email',
  'telegram': 'Telegram',
  'both': 'Email и Telegram'
};

function safeLocaleDate(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString('ru-RU', {
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit'
  });
}

const TelegramIcon = () => (
  <img src="/tg.svg" alt="TG" width="16" height="16" style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: 6 }} />
);

const EmailIcon = () => (
  <img src="/gmail.svg" alt="Gmail" width="16" height="16" style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: 6 }} />
);

const TasksTable = ({ title, rows, retryingIds, onRetry, isMobile }) => {
  const [hoveredId, setHoveredId] = useState(null);

  const getStatusBadge = (task, labelPrefix, icon) => {
    if (!task) {
      return (
        <div style={styles.channelStatusRow}>
          <span style={styles.channelName}>{icon}{labelPrefix}:</span>
          <span style={styles.noChannel}>—</span>
        </div>
      );
    }

    const map = {
      pending: { label: 'В ожидании', color: colors.warningText, bg: colors.warningBg },
      sent: { label: 'Отправлено', color: colors.successText, bg: colors.successBg },
      failed: { label: 'Ошибка', color: colors.errorText, bg: colors.errorBg },
      cancelled: { label: 'Отменена', color: colors.gray500, bg: colors.gray100 },
    };

    const info = map[task.status] || { label: task.status, color: colors.primary, bg: colors.gray100 };
    const isPending = task.status === 'pending';
    const isFailed = task.status === 'failed';

    return (
      <div style={styles.channelStatusRow}>
        <div style={styles.channelBadgeRow}>
          <span style={styles.channelName}>{icon}{labelPrefix}:</span>
          <Badge
            style={{
              fontSize: '11px',
              padding: '3px 8px',
              fontWeight: 600,
              background: info.bg,
              color: info.color,
              border: 'none',
            }}
          >
            {info.label}
          </Badge>
          
          {isFailed && (
            <button
              onClick={() => onRetry(task.id)}
              disabled={retryingIds[task.id]}
              style={{
                ...styles.retryBtnInline,
                backgroundColor: retryingIds[task.id] || hoveredId === task.id ? '#eaeaea' : 'transparent',
              }}
              onMouseEnter={() => setHoveredId(task.id)}
              onMouseLeave={() => setHoveredId(null)}
              title="Повторить отправку"
            >
              {retryingIds[task.id] ? (
                <Spinner size={10} />
              ) : (
                <RefreshCw size={12} color={colors.primary} />
              )}
            </button>
          )}
        </div>
        
        {isFailed && task.error_message && (
          <div style={styles.errorTextRow}>
            <span style={styles.errorValue}>{task.error_message}</span>
          </div>
        )}
        
        {task.sent_at && (
          <div style={styles.sentAtRow}>
            Отправлено: {safeLocaleDate(task.sent_at)}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={styles.tableContainer}>
      <h4 style={styles.tableTitle}>{title} — {rows.length}</h4>
      <div style={styles.tableWrapper}>
        <table style={styles.table}>
          <thead>
            <tr style={styles.thRow}>
              <th style={{ ...styles.th, textAlign: 'left' }}>Пользователь</th>
              <th style={{ ...styles.th, textAlign: 'left' }}>Статус отправки</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(row => {
              const u = row.telegramTask?.user || row.emailTask?.user;
              const fullName = [u?.first_name, u?.last_name].filter(Boolean).join(' ');
              const displayName = fullName || row.username;
              const subName = u?.username ? `@${u.username}` : `ID: ${row.userId}`;

              return (
                <tr key={row.userId} style={styles.tr}>
                  <td style={styles.tdUser}>
                    <div style={styles.userInfoCol}>
                      <span style={styles.userFullName}>{displayName}</span>
                      <span style={styles.userSubName}>{subName}</span>
                    </div>
                  </td>
                  <td style={styles.tdMeta}>
                    <div style={styles.channelsContainer}>
                      {getStatusBadge(row.telegramTask, 'Telegram', <TelegramIcon />)}
                      {getStatusBadge(row.emailTask, 'Email', <EmailIcon />)}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default function NewsletterDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const [newsletter, setNewsletter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState({ open: false, type: 'success', text: '' });
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [retryingIds, setRetryingIds] = useState({});

  const loadNewsletter = useCallback(async () => {
    try {
      setLoading(true);
      const data = await newslettersApi.detail(id);
      setNewsletter(data);
    } catch (err) {
      setNotice({ open: true, type: 'error', text: normalizeApiError(err) });
    } finally {
      setLoading(false);
    }
  }, [id]);

  const handleRetryTask = useCallback(async (taskId) => {
    setRetryingIds(prev => ({ ...prev, [taskId]: true }));
    
    // Optimistically update status to pending and clear error in local state
    setNewsletter(prev => {
      if (!prev || !prev.tasks) return prev;
      return {
        ...prev,
        tasks: prev.tasks.map(t => 
          t.id === taskId ? { ...t, status: 'pending', error_message: null } : t
        )
      };
    });

    try {
      await newslettersApi.retryTask(taskId);
      // Quiet background refresh of the newsletter data
      const data = await newslettersApi.detail(id);
      setNewsletter(data);
    } catch (err) {
      setNotice({ open: true, type: 'error', text: normalizeApiError(err) });
      // Re-fetch to restore correct state on error
      try {
        const data = await newslettersApi.detail(id);
        setNewsletter(data);
      } catch (_) {}
    } finally {
      setRetryingIds(prev => ({ ...prev, [taskId]: false }));
    }
  }, [id]);

  useEffect(() => {
    loadNewsletter();
  }, [loadNewsletter]);

  const handleCancelNewsletter = useCallback(async () => {
  setConfirmCancel(false);
  setCanceling(true);
  try {
    await newslettersApi.remove(id); // Теперь этот метод существует
    setNotice({ open: true, type: 'success', text: 'Рассылка успешно отменена.' });
    setTimeout(() => navigate('/newsletters'), 1500);
  } catch (err) {
    // ВАЖНО: берем только строку message
    const apiError = normalizeApiError(err);
    setNotice({
      open: true,
      type: 'error',
      text: apiError.message || 'Произошла неизвестная ошибка'
    });
  } finally {
    setCanceling(false);
  }
}, [id, navigate]);

  const usersWithTasks = useMemo(() => {
    if (!newsletter?.tasks) return [];
    const groups = {};
    newsletter.tasks.forEach(task => {
      const userId = task.user?.id || 'unknown';
      const username = task.user?.username || `ID: ${task.user?.id || 'unknown'}`;
      if (!groups[userId]) {
        groups[userId] = {
          userId,
          username,
          telegramTask: null,
          emailTask: null,
        };
      }
      if (task.channel === 'telegram') {
        groups[userId].telegramTask = task;
      } else if (task.channel === 'email') {
        groups[userId].emailTask = task;
      }
    });
    return Object.values(groups);
  }, [newsletter]);

  if (loading) return <div style={styles.center}><Spinner size={40} /></div>;
  if (!newsletter) return null;

  return (
    <div style={pageStyles.page}>
      <div style={pageStyles.container}>
        <button onClick={() => navigate('/newsletters')} style={styles.backBtn}>
          ← Назад к списку
        </button>

        <PageHeader
            title={`${newsletter.title}`}
            subtitle={`${STATUS_MAP[newsletter.status]?.label}`}
          isMobile={isMobile}

        />

        <div style={styles.mainContent}>
          {/* Левая колонка - Контент */}
          <div style={styles.leftCol}>
            <div style={styles.contentCard}>
               <div style={styles.channelRow}>
                <span style={styles.nsTitle}>{newsletter.title}</span>
              </div>

              <div
                style={styles.messageBody}
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(newsletter.message) }}
              />

              {newsletter.images?.length > 0 && (
                <div style={styles.imageGallery}>
                  {newsletter.images.map(img => (
                    <div key={img.id} style={styles.imageWrapper}>
                      <img src={newslettersApi.getFileUrl(img.image)} alt="" style={styles.img} />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Правая колонка - Параметры и Статистика */}
          <div style={styles.rightCol}>
            <div style={styles.sideCard}>
              <h3 style={styles.sideTitle}>Параметры</h3>
              <InfoBlock 
                label="Аудитория" 
                value={
                  newsletter.only_paid === 'paid' 
                    ? 'Только оплатившие' 
                    : newsletter.only_paid === 'unpaid' 
                      ? 'Только НЕ оплатившие' 
                      : 'Все пользователи'
                } 
              />
              <InfoBlock label="Запланировано на" value={safeLocaleDate(newsletter.scheduled_at) || 'Мгновенно'} />
              <InfoBlock label="Канал отправки" value={CHANNEL_LABELS[newsletter.channel]} />

              {newsletter.status === 'scheduled' && (
                <Button
                  variant="danger"
                  onClick={() => setConfirmCancel(true)}
                  loading={canceling}
                  fullWidth
                  style={{marginTop: spacing.md}}
                >
                  Отменить
                </Button>
              )}
            </div>

            {newsletter.status !== 'scheduled' && (
              <div style={styles.sideCard}>
                <h3 style={styles.sideTitle}>Статистика</h3>
                <div style={styles.statsList}>
                   <StatRow label="Всего" value={newsletter.total} />
                   <StatRow label="Успешно" value={newsletter.sent} color={colors.gray500} />
                   <StatRow label="Ошибки" value={newsletter.failed} color={colors.gray300} />
                </div>
              </div>
            )}
          </div>

            {/* Таблицы пользователей */}
            {newsletter.status !== 'scheduled' && usersWithTasks.length > 0 ? (
              <div style={styles.tablesSection}>
                <TasksTable
                  title="Отчет о доставке"
                  rows={usersWithTasks}
                  retryingIds={retryingIds}
                  onRetry={handleRetryTask}
                  isMobile={isMobile}
                />
              </div>
            ) : null}
          </div>


        </div>
      </div>

      <ConfirmModal
        open={confirmCancel}
        title="Отмена рассылки"
        message={`Отменить рассылку "${newsletter.title}"?`}
        description="Это действие нельзя будет отменить"
        confirmText="Да, отменить"
        onConfirm={handleCancelNewsletter}
        onClose={() => setConfirmCancel(false)}
      />

      <NoticeModal
        open={notice.open}
        type={notice.type}
        text={notice.text}
        onClose={() => setNotice({ ...notice, open: false })}
      />
    </div>
  );
}

const InfoBlock = ({ label, value }) => (
  <div style={styles.infoBlock}>
    <span style={styles.infoLabel}>{label}</span>
    <span style={styles.infoValue}>{value}</span>
  </div>
);

const StatRow = ({ label, value, color }) => (
  <div style={styles.statRow}>
    <span style={styles.infoLabel}>{label}</span>
    <span style={{ ...styles.statValue, color: color || colors.primary }}>{value}</span>
  </div>
);

// TasksTable is defined above as a component

const styles = {
  center: { display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' },
  topNav: { marginBottom: spacing.sm },
  backBtn: {
    background: 'none',
    border: 'none',
    color: colors.gray400,
    fontWeight: 600,
    cursor: 'pointer',
    marginBottom: '16px',
    fontSize: '14px',
    fontFamily: 'Montserrat',
  },
  mainContent: {
    display: 'flex',
    flexDirection: window.innerWidth < 992 ? 'column' : 'row',
    marginTop: spacing.lg
  },
  leftCol: { flex: 2, minWidth: 0 },
  rightCol: { flex: 1, display: 'flex', flexDirection: 'column',},

  contentCard: {
    background: colors.white,
    padding: spacing.xl,
    borderRadius: borderRadius.card,
    boxShadow: shadows.card,
    border: `1px solid ${colors.gray100}`,
    marginBottom: spacing.xl
  },
  channelRow: { display: 'flex', justifyContent: 'space-between', marginBottom: spacing.md, alignItems: 'center' },
  channelLabel: { fontSize: '13px', fontWeight: 800, color: colors.gray400, textTransform: 'uppercase', textAlign: 'center' },
  nsTitle: { fontSize: '15px', fontWeight: 700, color: colors.primary, },

  messageBody: {
    fontSize: '14px',
    lineHeight: 1.6,
    color: colors.primary,
    fontWeight: 500,
    whiteSpace: 'pre-wrap',
    marginBottom: spacing.xl
  },
  imageGallery: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: spacing.md },
  imageWrapper: { aspectRatio: '1', borderRadius: borderRadius.medium, overflow: 'hidden', border: `1px solid ${colors.gray100}` },
  img: { width: '100%', height: '100%', objectFit: 'cover' },

  sideCard: {
    background: colors.white,
    padding: spacing.lg,
    borderRadius: borderRadius.card,
    boxShadow: shadows.card,
    border: `1px solid ${colors.gray100}`,
    marginBottom: spacing.md,
  },
  sideTitle: { fontSize: '14px', fontWeight: 800, color: colors.primary, textTransform: 'uppercase', marginBottom: spacing.md, borderBottom: `1px solid ${colors.gray50}`, paddingBottom: spacing.xs },

  infoBlock: { marginBottom: spacing.md, display: 'flex', flexDirection: 'column' },
  infoLabel: { fontSize: '12px', fontWeight: 800, color: colors.gray400, textTransform: 'uppercase', marginBottom: 2 },
  infoValue: { fontSize: '14px', fontWeight: 600, color: colors.primary },

  statsList: { display: 'flex', flexDirection: 'column', gap: spacing.sm },
  statRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  statValue: { fontSize: '16px', fontWeight: 800 },

  tablesSection: { display: 'flex', flexDirection: 'column', gap: spacing.xl },
  tableContainer: { },
  tableTitle: { fontSize: '14px', fontWeight: 800, color: colors.primary, textTransform: 'uppercase', marginBottom: spacing.md, textAlign: 'center' },
  tableWrapper: {
    background: colors.white,
    borderRadius: borderRadius.card,
    border: `1px solid ${colors.gray100}`,
    overflow: 'visible'
  },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { padding: '12px 20px', background: colors.gray50, color: colors.gray500, fontSize: '10px', fontWeight: 800, textTransform: 'uppercase' },
  tr: { borderBottom: `1px solid ${colors.gray50}` },
  tdUser: { padding: '12px 20px', fontSize: '14px', fontWeight: 700, color: colors.primary },
  tdMeta: { padding: '12px 20px', fontSize: '12px', fontWeight: 600 },
  userInfoCol: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  userFullName: {
    fontSize: '14px',
    fontWeight: 700,
    color: colors.primary,
  },
  userSubName: {
    fontSize: '11px',
    fontWeight: 600,
    color: colors.gray400,
  },
  retryBtnInline: {
    border: 'none',
    borderRadius: '50%',
    padding: '6px',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background-color 0.2s',
  },
  channelStatusRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    marginBottom: spacing.xs,
    padding: '8px 12px',
    borderRadius: '6px',
    backgroundColor: '#fcfcfc',
    border: '1px solid #f0f0f0',
  },
  channelBadgeRow: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.sm,
    flexWrap: 'wrap'
  },
  channelName: {
    fontWeight: 700,
    fontSize: '13px',
    color: colors.primary,
    minWidth: '70px',
  },
  noChannel: {
    fontSize: '12px',
    color: colors.gray400,
    fontStyle: 'italic',
  },
  errorTextRow: {
    fontSize: '11px',
    color: colors.danger,
    marginTop: '2px',
    display: 'flex',
    flexDirection: 'column',
  },
  errorValue: {
    color: colors.error || '#DC2626',
    fontSize: '11px',
    fontWeight: 600,
    marginTop: '2px',
    wordBreak: 'break-word',
  },
  sentAtRow: {
    fontSize: '11px',
    color: colors.gray400,
    marginTop: '2px',
  },
  channelsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    width: '100%',
  },
  thRow: {
    backgroundColor: colors.gray50,
    borderBottom: `2px solid ${colors.gray100}`,
  },

  tooltip: {
    position: 'absolute', bottom: 'calc(100% + 10px)', left: '50%', transform: 'translateX(-50%)',
    backgroundColor: '#333', color: '#fff', padding: '6px 10px', borderRadius: '6px', fontSize: '11px',
    width: '160px', textAlign: 'center', zIndex: 10, boxShadow: shadows.md, lineHeight: 1.3
  },
  tooltipArrow: {
    position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)',
    borderWidth: '5px', borderStyle: 'solid', borderColor: '#333 transparent transparent transparent'
  },
};